import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Image, LinearProgress } from '@mafc/trolly';
import { useDispatch } from 'react-redux';

import { AppliedVoucher } from 'network/response/ICart';
import { createMarkup } from 'utils/html.util';
import { getDiscountAmount } from '../Promo/Promo.helper';
import { IAvailablePromotions } from '../AvailablePromotions/redux/AvailablePromotions.reducer';
import AvailablePromotionsTabs from './AvailablePromotionsTabs';
import OrderCancelled from 'assets/order-cancelled.png';
import InfoContainer from 'common/InfoContainer/InfoContainer';
import { IPromo } from 'pages/Checkout/redux/reducer/promo.reducer';
import { fetchAvailablePromotions } from '../AvailablePromotions/redux/AvailablePromotions.action';

import * as S from './AvailablePromotionsDrawer.style';

const AvailablePromotionsDrawerBody: FC<{
  availablePromotions: IAvailablePromotions;
  appliedPromo: AppliedVoucher | null;
  promoState: IPromo;
  onPromoRemove: () => void;
  enterCodeBtnClickHandler: () => void;
}> = ({
  availablePromotions,
  appliedPromo,
  promoState,
  onPromoRemove,
  enterCodeBtnClickHandler,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const refreshOnError = () => {
    dispatch(fetchAvailablePromotions(appliedPromo ? appliedPromo.code : ''));
  };

  return (
    <S.AvailablePromotionsDrawerBody>
      <S.SelectOneVoucher>{t('promo.discountOneOrder')}</S.SelectOneVoucher>

      {appliedPromo ? (
        <S.AppliedPromo>
          <Box>
            <Box>
              <Box as="span" fontWeight={6} mr={1}>
                {appliedPromo.voucherCode}
              </Box>
              {t('promo.applied')}
            </Box>

            <Box
              dangerouslySetInnerHTML={createMarkup(
                t('promo.haveSavedMessage', {
                  discount: getDiscountAmount(appliedPromo),
                })
              )}
            />
          </Box>

          <S.RemovePromoButton
            variant="secondary"
            onClick={onPromoRemove}
            disabled={promoState.loading}
          >
            {t('remove')}
          </S.RemovePromoButton>
        </S.AppliedPromo>
      ) : (
        <Flex justifyContent="space-between" alignItems="center" mb={3}>
          <Box>{t('promo.haveDiscountCode')}</Box>

          <S.EnterCodeButton
            data-testid="enter_promo_code_btn"
            onClick={enterCodeBtnClickHandler}
            variant="custom"
            preventTextTransform
          >
            {t('promo.enterCode')}
          </S.EnterCodeButton>
        </Flex>
      )}

      {availablePromotions.error && (
        <>
          <Flex justifyContent="center">
            <Image
              mb={3}
              url={OrderCancelled}
              name="no-available-promo"
              height="250px"
              width="auto"
            />
          </Flex>

          <InfoContainer style={{ marginBottom: '16px' }}>
            <Box>
              <Box mb={4}>{t('promo.availablePromoError1')}</Box>
              {t('promo.availablePromoError2')}
            </Box>
          </InfoContainer>

          <S.RefreshButton onClick={refreshOnError} testid="refresh-button" variant="secondary">
            {t('promo.refresh')}
          </S.RefreshButton>
        </>
      )}

      {availablePromotions.tabData.data.length > 0 && (
        <AvailablePromotionsTabs
          availablePromotions={availablePromotions}
          appliedPromo={appliedPromo}
          promoState={promoState}
        />
      )}

      {(promoState.loading || availablePromotions.loading) && <LinearProgress position="bottom" />}
    </S.AvailablePromotionsDrawerBody>
  );
};

export default AvailablePromotionsDrawerBody;
